import { orderSciEventsApi } from 'api';
import { env } from 'config';
import { EventName } from 'enums';
import { OrderSciEventData } from 'types';
import { authUtils, gaUtils } from 'utils';

import { sessionStorageUtils } from './session-storage.utils';

// ── Session identity ──────────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = 'sci_session_id';
const EVENT_QUEUE_KEY = 'sci_event_queue';
const QUEUE_TTL_MS = 30 * 60 * 1000;
const QUEUE_MAX_SIZE = 50;

const generateId = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

export const initSessionId = (): string => {
  const existing = sessionStorageUtils.get(SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const id = generateId();
  sessionStorageUtils.set(SESSION_STORAGE_KEY, id);
  return id;
};

let sessionId: string | undefined;

const getSessionId = (): string => {
  if (!sessionId) {
    sessionId = initSessionId();
  }
  return sessionId;
};

// ── Stop-after-success guard ──────────────────────────────────────────────────

let eventsStopped = false;

const stopEvents = () => {
  eventsStopped = true;
};

// ── URL helpers ───────────────────────────────────────────────────────────────

const getOrderIdFromUrl = (): string => {
  try {
    const parts = window.location.pathname.split('/');
    return parts.at(-2) || '';
  } catch {
    return '';
  }
};

// ── localStorage queue ────────────────────────────────────────────────────────

type QueueEntry = { orderId: string; data: OrderSciEventData; savedAt: number };

const readQueue = (): QueueEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(EVENT_QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeQueue = (entries: QueueEntry[]) => {
  try {
    localStorage.setItem(EVENT_QUEUE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage quota exceeded — drop silently
  }
};

const saveToEventQueue = (orderId: string, data: OrderSciEventData) => {
  const entries = readQueue();
  const now = Date.now();
  const fresh = entries.filter((e) => now - e.savedAt < QUEUE_TTL_MS);
  writeQueue(
    [...fresh, { orderId, data, savedAt: now }].slice(-QUEUE_MAX_SIZE),
  );
};

const drainEventQueue = async (currentOrderId: string) => {
  const entries = readQueue();
  const now = Date.now();
  const forCurrentOrder = entries.filter(
    (e) => e.orderId === currentOrderId && now - e.savedAt < QUEUE_TTL_MS,
  );
  const remaining = entries.filter(
    (e) => e.orderId !== currentOrderId && now - e.savedAt < QUEUE_TTL_MS,
  );

  if (forCurrentOrder.length === 0) {
    return;
  }

  try {
    await orderSciEventsApi.createBatch(
      currentOrderId,
      forCurrentOrder.map((e) => e.data),
    );
    writeQueue(remaining);
  } catch {
    // leave in queue for next retry
  }
};

// ── Pending events (pre-auth, in-memory) ─────────────────────────────────────

let pendingEvents: OrderSciEventData[] = [];

// ── Core event sender ─────────────────────────────────────────────────────────

const buildPayload = (data: OrderSciEventData): OrderSciEventData => ({
  ...data,
  sessionId: getSessionId(),
  clientTimestamp: new Date().toISOString(),
});

const trackEvent = (
  data: OrderSciEventData,
  targets: { ga?: boolean; app?: boolean } = { ga: true, app: true },
) => {
  if (eventsStopped) {
    return;
  }

  const orderId = getOrderIdFromUrl();
  if (!orderId) {
    return;
  }

  const payload = buildPayload(data);

  if (targets.app) {
    if (authUtils.getAccessToken()) {
      orderSciEventsApi
        .create(orderId, payload)
        .catch(() => saveToEventQueue(orderId, payload));
    } else {
      pendingEvents.push(payload);
    }
  }

  if (targets.ga) {
    const { event, ...rest } = data;
    gaUtils.trackEvent(event, rest);
  }
};

// ── Flush pending (called after successful auth) ──────────────────────────────

const flushPendingEvents = () => {
  const orderId = getOrderIdFromUrl();
  if (!orderId) {
    return;
  }

  const events = pendingEvents;
  pendingEvents = [];
  events.forEach((data) =>
    orderSciEventsApi
      .create(orderId, data)
      .catch(() => saveToEventQueue(orderId, data)),
  );

  drainEventQueue(orderId).catch(() => {});
};

// ── Send before page unload (fetch keepalive) ─────────────────────────────────

const sendEventBeforeUnload = (data: OrderSciEventData) => {
  if (eventsStopped) {
    return;
  }
  const orderId = getOrderIdFromUrl();
  const token = authUtils.getAccessToken();
  if (!orderId || !token) {
    return;
  }

  const payload = buildPayload(data);
  const url = `${env.apiUrl}/v1/customer/orders/${orderId}/sci-events`;

  try {
    fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // best-effort: keepalive fetch failed synchronously
  }
};

// ── Convenience wrappers ──────────────────────────────────────────────────────

const trackException = (options?: { message?: string; stack?: string }) => {
  trackEvent({
    event: EventName.Exception,
    message: options?.message,
    stack: options?.stack,
  });
};

export const eventUtils = {
  trackEvent,
  flushPendingEvents,
  trackException,
  sendEventBeforeUnload,
  stopEvents,
};
